import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import type { AssetStatusChange, Policy, PolicyVehicle } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDollar, unformatDollar } from "@/lib/formatters";
import { useToast } from "@/hooks/use-toast";
import { useUpdatePolicyVehicles, useUpdatePolicyTrailers } from "@/lib/api";
import { PolicyMultiSelect } from "./PolicyMultiSelect";
import { AssetStatusSelect, type AssetLifecycleStatus } from "./AssetStatusSelect";
import { apiFetch } from "@/lib/queryClient";
import {
  defaultPolicySelection,
  planAssetLinkWrites,
  policiesLinkedToAsset,
  vehicleIdentity,
} from "./asset-helpers";

// CSR-1458 — a stored amount of "0" / "0.00" means "no coverage", not an
// explicit zero. Treat it as empty so the input renders its placeholder rather
// than a misleading "$0" on a vehicle without Physical Damage coverage.
// The stored JSONB can carry an amount as a number (e.g. Progressive `value`)
// even though the type says string — coerce like formatDollar/unformatDollar do.
function normalizeStoredAmount(raw: string | number | null | undefined): string {
  const value = String(raw ?? "").trim();
  if (value === "") return "";
  const n = parseFloat(value.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? value : "";
}

export type VehicleDialogMode = "powerUnit" | "trailer";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: VehicleDialogMode;
  policies: Policy[];
  /** When set, the dialog is in edit mode for this vehicle entry. */
  editData: PolicyVehicle | null;
  /** Required when editData is non-null — identifies which policy the entry lives on. */
  editPolicyId: string | null;
}

interface FormErrors {
  vin?: string;
  make?: string;
  year?: string;
  model?: string;
  policies?: string;
}

export function VehicleDialog({ open, onOpenChange, mode, policies, editData, editPolicyId }: Props) {
  const { toast } = useToast();
  const updateVehicles = useUpdatePolicyVehicles();
  const updateTrailers = useUpdatePolicyTrailers();

  const isEdit = !!editData;
  const isTrailer = mode === "trailer";
  const arrayKey: "vehicles" | "trailers" = isTrailer ? "trailers" : "vehicles";

  const [vin, setVin] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [truckType, setTruckType] = useState(isTrailer ? "Trailer" : "");
  const [value, setValue] = useState("");
  const [deductibleComp, setDeductibleComp] = useState("");
  const [deductibleCollision, setDeductibleCollision] = useState("");
  const [radius, setRadius] = useState("");
  const [status, setStatus] = useState<AssetLifecycleStatus>("active");
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [decoding, setDecoding] = useState(false);
  // Last VIN we decoded, so re-blurring the same value doesn't clobber overrides.
  const lastDecodedVinRef = useRef<string>("");

  // CSR-2335 — the "Related Policies" picker offers every policy on the insured,
  // whatever its status, so it can be linked to (or unlinked from) any term.
  const showPicker = policies.length > 0;

  useEffect(() => {
    if (!open) return;
    if (editData) {
      setVin(editData.vin ?? "");
      setYear(editData.year != null ? String(editData.year) : "");
      setMake(editData.make ?? "");
      setModel(editData.model ?? "");
      setTruckType(editData.truckType ?? (isTrailer ? "Trailer" : ""));
      setValue(normalizeStoredAmount(editData.value));
      setDeductibleComp(normalizeStoredAmount(editData.deductibleComp));
      setDeductibleCollision(normalizeStoredAmount(editData.deductibleCollision));
      setRadius(editData.radius ?? "");
      setStatus(editData.removedAt ? "removed" : "active");
      // Pre-select every shown policy this asset is already linked to. Fall back
      // to the origin policy if the identity match finds nothing (e.g. VIN-less).
      const editKey = vehicleIdentity(editData);
      const linked = editKey
        ? policiesLinkedToAsset<PolicyVehicle>(policies, arrayKey, vehicleIdentity, editKey)
        : [];
      setSelectedPolicyIds(linked.length > 0 ? linked : editPolicyId ? [editPolicyId] : []);
    } else {
      setVin("");
      setYear("");
      setMake("");
      setModel("");
      setTruckType(isTrailer ? "Trailer" : "");
      setValue("");
      setDeductibleComp("");
      setDeductibleCollision("");
      setRadius("");
      setStatus("active");
      setSelectedPolicyIds(defaultPolicySelection(policies));
    }
    // Seed with the current VIN so opening edit mode (or reopening) does not
    // auto-decode on the first blur unless the agent actually changes the VIN.
    lastDecodedVinRef.current = (editData?.vin ?? "").trim();
    setDecoding(false);
    setErrors({});
    // `policies.length` (not `policies`) so a TanStack Query background refetch
    // — new array identity, same length — doesn't reset in-progress form edits.
  }, [open, editData, editPolicyId, policies.length, arrayKey, isTrailer]);

  const isPending = updateVehicles.isPending || updateTrailers.isPending;

  // CSR-1146 — decode the VIN on blur and auto-populate Year/Make/Model/Body Type
  // (QF/PX parity). Fields stay editable so the agent can override. Body Type is
  // populated from NHTSA BodyClass for both Power Units and Trailers.
  async function handleVinBlur() {
    const clean = vin.trim();
    if (!clean || clean === lastDecodedVinRef.current) return;
    lastDecodedVinRef.current = clean;
    setDecoding(true);
    try {
      const res = await apiFetch(`/api/vin/decode?vin=${encodeURIComponent(clean)}`, {
        credentials: "include",
      });
      if (!res.ok) {
        setErrors((prev) => ({
          ...prev,
          vin: "Could not decode VIN. Check the VIN and try again.",
        }));
        return;
      }
      const data = (await res.json()) as {
        year?: string;
        make?: string;
        model?: string;
        bodyType?: string;
      };
      if (data.year) setYear(String(data.year));
      if (data.make) setMake(data.make);
      if (data.model) setModel(data.model);
      if (data.bodyType) setTruckType(data.bodyType);
      setErrors((prev) => ({ ...prev, vin: undefined }));
    } catch {
      setErrors((prev) => ({
        ...prev,
        vin: "Could not decode VIN. Check the VIN and try again.",
      }));
    } finally {
      setDecoding(false);
    }
  }

  function handleSubmit() {
    const next: FormErrors = {};
    // CSR-1184 — VIN is optional. When it is missing the asset must be
    // identifiable manually: make + year + model for a Power Unit, make + year
    // for a Trailer (model isn't tracked for trailers).
    if (!vin.trim()) {
      if (!make.trim()) next.make = "Required";
      if (!year.trim()) next.year = "Required";
      if (!isTrailer && !model.trim()) next.model = "Required";
    }
    if (selectedPolicyIds.length === 0) next.policies = "Select at least one policy.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const nowIso = new Date().toISOString();
    const newVin = vin.trim().toUpperCase();
    // Match existing entries by the asset's ORIGINAL identity so a VIN edit
    // still finds the rows to update/unlink; new identity is written via buildEntry.
    const matchKey = (isEdit && editData ? vehicleIdentity(editData) : null) ?? newVin;

    const buildEntry = (existing: PolicyVehicle | null): PolicyVehicle => ({
      // Preserve carrier/DC-owned fields not represented in the form
      // (databaseId, vehicleSpecificCoverages, …). When appending to a newly
      // linked policy there is no existing row, so seed from the edited entry.
      ...(existing ?? editData ?? {}),
      vin: newVin || undefined,
      year: year.trim() ? Number(year.trim()) : undefined,
      make: make.trim() || undefined,
      model: model.trim() || undefined,
      truckType: truckType.trim() || undefined,
      value: value.trim() || undefined,
      deductibleComp: deductibleComp.trim() || undefined,
      deductibleCollision: deductibleCollision.trim() || undefined,
      radius: radius.trim() || undefined,
      addedAt: existing?.addedAt ?? nowIso,
      removedAt: null,
    });

    const writes = planAssetLinkWrites<PolicyVehicle>({
      // An Add only writes the ticked terms: an unticked term was never looked
      // at, so it must not be read as "unlink" (CSR-2335). Edit pre-checks every
      // term holding the asset, so there unticking is a deliberate unlink.
      policies: isEdit ? policies : policies.filter((p) => selectedPolicyIds.includes(p.id)),
      arrayKey,
      identityOf: vehicleIdentity,
      matchKey,
      buildEntry,
      selectedPolicyIds,
    });

    // CSR-1404 — only an explicit Status change carries a directive; the merge
    // ignores `removedAt` in the submitted array, so this is what reactivates or
    // removes the edited asset. Identity targets the saved (post-edit) entry.
    const statusChanged = isEdit && (status === "removed") !== !!editData?.removedAt;
    const assetStatusChange: AssetStatusChange | undefined = statusChanged
      ? {
          arrayKey,
          match: {
            databaseId: editData?.databaseId,
            vin: newVin || editData?.vin || undefined,
            addedAt: editData?.addedAt,
          },
          removed: status === "removed",
        }
      : undefined;
    // Send the directive on a single write; the server propagates the status to
    // every policy the asset is on (the badge is a cross-policy aggregate).
    const selected = new Set(selectedPolicyIds);
    const directiveTargetId = writes.find((w) => selected.has(w.policyId))?.policyId;

    Promise.all(
      writes.map((w) => {
        const directive =
          assetStatusChange && w.policyId === directiveTargetId ? assetStatusChange : undefined;
        return isTrailer
          ? updateTrailers.mutateAsync({
              id: w.policyId,
              trailers: w.nextArray,
              assetStatusChange: directive,
            })
          : updateVehicles.mutateAsync({
              id: w.policyId,
              vehicles: w.nextArray,
              assetStatusChange: directive,
            });
      }),
    )
      .then(() => {
        const linkedCount = selectedPolicyIds.length;
        toast({
          title: isEdit ? "Updated" : "Added",
          description: isEdit
            ? undefined
            : `Linked to ${linkedCount} ${linkedCount === 1 ? "policy" : "policies"}. Note: this list syncs from the carrier; your changes may be replaced on next sync.`,
        });
        onOpenChange(false);
      })
      .catch((err) => {
        toast({
          title: "Failed",
          description: err instanceof Error ? err.message : "Could not save vehicle.",
          variant: "destructive",
        });
      });
  }

  const title = isEdit
    ? isTrailer ? "Edit Trailer" : "Edit Power Unit"
    : isTrailer ? "Add Trailer" : "Add Power Unit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vehicle-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Edits apply immediately. Carrier scrape may overwrite on next sync."
              : "Added entries may be replaced when the carrier next scrapes the policy."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showPicker && (
            <PolicyMultiSelect
              policies={policies}
              selected={selectedPolicyIds}
              onChange={setSelectedPolicyIds}
              testId="vehicle-dialog-policy-picker"
              checkboxTestId={(id) => `vehicle-policy-checkbox-${id}`}
              error={errors.policies}
            />
          )}

          <div>
            <Label htmlFor="vehicle-vin">VIN</Label>
            <Input
              id="vehicle-vin"
              data-testid="vehicle-vin-input"
              value={vin}
              onChange={(e) => {
                setVin(e.target.value);
                // A now-optional empty VIN shouldn't keep a stale decode error.
                if (!e.target.value.trim()) setErrors((prev) => ({ ...prev, vin: undefined }));
              }}
              onBlur={handleVinBlur}
              className={errors.vin ? "border-destructive" : ""}
            />
            <p data-testid="vehicle-vin-hint" className="text-xs text-muted-foreground mt-1">
              {isTrailer
                ? "Optional. If left blank, enter Make and Year manually."
                : "Optional. If left blank, enter Make, Year and Model manually."}
            </p>
            {decoding && (
              <p
                data-testid="vehicle-vin-decoding"
                className="text-xs text-muted-foreground mt-1 flex items-center gap-1"
              >
                <Loader2 className="w-3 h-3 animate-spin" /> Decoding VIN…
              </p>
            )}
            {errors.vin && (
              <p data-testid="vehicle-vin-error" className="text-xs text-destructive mt-1">
                {errors.vin}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vehicle-year">Year</Label>
              <Input
                id="vehicle-year"
                data-testid="vehicle-year-input"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                inputMode="numeric"
                className={errors.year ? "border-destructive" : ""}
              />
              {errors.year && (
                <p data-testid="vehicle-year-error" className="text-xs text-destructive mt-1">
                  {errors.year}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="vehicle-make">Make</Label>
              <Input
                id="vehicle-make"
                data-testid="vehicle-make-input"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                className={errors.make ? "border-destructive" : ""}
              />
              {errors.make && (
                <p data-testid="vehicle-make-error" className="text-xs text-destructive mt-1">
                  {errors.make}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="vehicle-model">Model</Label>
              <Input
                id="vehicle-model"
                data-testid="vehicle-model-input"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className={errors.model ? "border-destructive" : ""}
              />
              {errors.model && (
                <p data-testid="vehicle-model-error" className="text-xs text-destructive mt-1">
                  {errors.model}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="vehicle-truck-type">Body Type</Label>
              <Input
                id="vehicle-truck-type"
                data-testid="vehicle-truck-type-input"
                value={truckType}
                onChange={(e) => setTruckType(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vehicle-value">Stated Value</Label>
              <Input
                id="vehicle-value"
                data-testid="vehicle-value-input"
                value={value ? formatDollar(value) : ""}
                onChange={(e) => setValue(unformatDollar(e.target.value))}
                inputMode="decimal"
                placeholder="$0"
              />
            </div>
            <div>
              <Label htmlFor="vehicle-deductible-comp">Comp Deductible</Label>
              <Input
                id="vehicle-deductible-comp"
                data-testid="vehicle-deductible-comp-input"
                value={deductibleComp ? formatDollar(deductibleComp) : ""}
                onChange={(e) => setDeductibleComp(unformatDollar(e.target.value))}
                inputMode="decimal"
                placeholder="$0"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="vehicle-deductible-collision">Collision Deductible</Label>
              <Input
                id="vehicle-deductible-collision"
                data-testid="vehicle-deductible-collision-input"
                value={deductibleCollision ? formatDollar(deductibleCollision) : ""}
                onChange={(e) => setDeductibleCollision(unformatDollar(e.target.value))}
                inputMode="decimal"
                placeholder="$0"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="vehicle-radius">Radius (mi)</Label>
            <Input
              id="vehicle-radius"
              data-testid="vehicle-radius-input"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              placeholder="e.g. 150 mi or Unlimited"
            />
          </div>

          {isEdit && (
            <div>
              <Label htmlFor="vehicle-status">Status</Label>
              <AssetStatusSelect value={status} onChange={setStatus} testId="vehicle-status-select" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            data-testid="vehicle-dialog-submit"
            className="cursor-pointer"
          >
            {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
